import { createSlice } from "@reduxjs/toolkit";
import { isEqual, merge } from "lodash";
import { updateWheelSegmentsNodes } from "../../utils/segmentBuilderUtils";
// Initial state with history tracking
const initialState = {
  wheel_name: "",
  take_screenshot: false,
  isSavingWheel: false,
  wheel_preview: "",
  type: 2, // custom wheel
  version: 2, // new editor
  selected_segment_char_count: 0, // New state for tracking character count
  business_box: {
    id: null,
    user: null,
    name: "",
    business_email: "",
    business_url: "",
    is_visible: null,
    is_base: null,
    business_image: "",
    advert_image: "",
    business_title: "",
    description: "",
    business_link: [],
  },
  automatic_resizing: false,
  content_heights: {},
  is_primary: null,
  visibility: 1,
  is_locked: false,
  lock_type: {},
  show_business_box: null,
  is_cloning: false,
  description: "",
  sub_title: "",
  background: {
    type: "",
    value: "",
  },
  image_links: [],
  video_links: [],
  document_links: [],
  audio_links: [],
  starred_links: [],
  wheel_image: "",
  wheel_center_media: {
    type: "image",
    url: "",
  },
  segment_icon: "blank",
  wheel_segments: [],
  selectedMenu: 1,
  designTabTarget: null,
  isSidePaneVisible: false,
  isToolBarVisible: false,
  isSegmentSettingVisible: false,
  is_rotate_box_open: false,
  froala_focus: null,
  lock_type: {
    description: "test lock type",
    access_code_lock: {
      is_active: true,
      access_codes: [],
    },
    permission_lock: {
      is_active: true,
      members: [],
      emails: [],
    },
  },

  is_clonable: true,
  is_live: false,
  is_draft: false,
  // minors-13plus B.14: the creator's maturity self-label. "general" (not
  // "mature") is the default, so a wheel is never silently 18+. Declared here
  // so addWheelData's edited-field diff can see it as a first-class key when
  // the server hydrates one in.
  content_rating: "general",
  isGlobalSettingsApplied: false,
  isGlobalCornerRoundingApplied: false,
  isGlobalBorderThicknessApplied: false,
  isGlobalMarginApplied: false,
  isGlobalHeadingPropsApplied: false,
  isGlobalParagraphPropsApplied: false,
  isGlobalLineHeightApplied: false,
  globalSettings: {
    headingProps: {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 20,
      resetKey: 0,
    },
    paragraphProps: {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 14,
      resetKey: 0,
    },
    lineHeight: 1.15,
    cornerRounding: 8,
    borderThickness: 8,
    margin: {
      top: 20,
      bottom: 20,
      left: 20,
      right: 20,
    },
  },
};

// Initial state with history tracking
const initialHistoryState = {
  history: [initialState], // This will store all past states
  currentIndex: 0, // Tracks the current state index in history
  isUndoRedoAction: false, // Flag to track undo/redo actions
  hasUnsavedChanges: false, // Track if there are unsaved changes
  lastSavedIndex: 0, // Track the history index of the last save
  // fix-recordhistorysnapshot-missing-stg: gates withHistory()/withHistoryIgnoringSelection()
  // (redux/actions/history.js) re-hydration-echo suppression window; false the rest of the time.
  historySuppressed: false,
  // STAGING PORT GAP (2026-09-18): the resize-mode UI was ported to this branch
  // without its redux state. Both fields are pure ephemeral UI kept OUTSIDE
  // undo/redo history - an undo must never flip which tool the user is holding.
  // V1-RESIZE: per-Segment Settings pane showing the pure per-Segment resize view.
  segmentResizeView: false,
  // W-A0: the user explicitly entered "Resize Segment" mode via the side-pane
  // toggle. Default false; the builder resets it on unmount so the mode never
  // persists across a fresh open.
  resizeSegmentMode: false,
};

/**
 * Helper function to get the present state based on the currentIndex
 */
export const getPresentState = (state) =>
  state.history[state.currentIndex] || {};

/**
 * Helper function to push the present state into the history and update the index.
 */
export const pushToHistory = (state) => {
  const newPresent = { ...getPresentState(state) }; // Copy the present state
  const previousPresent = state.history[state.currentIndex]; // Get the current state in history

  // Only push to history if the new present state is different from the previous one
  if (!isEqual(newPresent, previousPresent)) {
    const newHistory = state.history.slice(0, state.currentIndex + 1); // Trim any future history
    newHistory.push(newPresent); // Add the new present state
    state.history = newHistory;
    state.currentIndex = newHistory.length - 1; // Move currentIndex to the latest state
    console.log("History updated with new present state.");
  } else {
    console.log("No state change, history not updated.");
  }
};

const segmentBuilder = createSlice({
  name: "segmentBuilder",
  initialState: initialHistoryState,
  reducers: {
    // Action to push current state to history
    pushToHistoryAction: (state) => {
      if (state.isUndoRedoAction) return; // Don't push to history during undo/redo
      console.log("CURRENT INDEX - Pushing to History", state.currentIndex);

      const newPresent = { ...getPresentState(state) }; // Copy the present state
      const newHistory = state.history.slice(0, state.currentIndex + 1); // Trim future history
      newHistory.push(newPresent); // Add new present state
      state.history = newHistory;
      state.currentIndex = newHistory.length - 1; // Set current index to latest state

      // Set hasUnsavedChanges to true whenever history changes
      if (state.currentIndex !== state.lastSavedIndex) {
        state.hasUnsavedChanges = true;
      }
    },

    // fix-recordhistorysnapshot-missing-stg: redux/actions/history.js's withHistory() /
    // withHistoryIgnoringSelection() thunks already ran the mutating action and their own
    // before/after isEqual diff BEFORE dispatching this — state.history[currentIndex] already
    // holds the new ("after") present state by the time this reducer runs, so committing it as
    // a new checkpoint is the exact same operation as pushToHistoryAction, just entered from a
    // different call path (one that does its own dirty-check up front instead of relying on
    // pushToHistoryAction's isUndoRedoAction guard). The action's `before`-snapshot payload is
    // accepted for call-site documentation but isn't needed here: staging's history model is a
    // flat array + currentIndex (not a past/present/future split), so nothing needs re-inserting.
    recordHistorySnapshot: (state) => {
      const newPresent = { ...getPresentState(state) };
      const newHistory = state.history.slice(0, state.currentIndex + 1);
      newHistory.push(newPresent);
      state.history = newHistory;
      state.currentIndex = newHistory.length - 1;

      if (state.currentIndex !== state.lastSavedIndex) {
        state.hasUnsavedChanges = true;
      }
    },

    // fix-recordhistorysnapshot-missing-stg: raised around undo/redo (dispatchSuppressed in
    // redux/actions/history.js) so the reactive echoes an undo/redo's own re-render triggers are
    // applied but never recorded as a second phantom checkpoint.
    setHistorySuppressed: (state, action) => {
      state.historySuppressed = action.payload;
    },

    undo: (state) => {
      if (state.currentIndex > 0) {
        state.isUndoRedoAction = true; // Set flag
        state.currentIndex -= 1; // Move back in history
        console.log("CURRENT INDEX UNDO", state.currentIndex);
        state.isUndoRedoAction = false; // Reset after undo

        // Update hasUnsavedChanges state
        state.hasUnsavedChanges = state.currentIndex !== state.lastSavedIndex;
      }
    },

    redo: (state) => {
      if (state.currentIndex < state.history.length - 1) {
        state.isUndoRedoAction = true; // Set flag
        state.currentIndex += 1; // Move forward in history
        console.log("CURRENT INDEX REDO", state.currentIndex);
        state.isUndoRedoAction = false; // Reset after redo

        // Update hasUnsavedChanges state
        state.hasUnsavedChanges = state.currentIndex !== state.lastSavedIndex;
      }
    },

    setType: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].type = action.payload;
    },

    setBusinessBoxWheel: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].business_box = action.payload;
    },

    setIsPrimary: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_primary = action.payload;
    },

    setIsPublic: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_public = action.payload;
    },

    setIsLocked: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_locked = action.payload;
    },

    setWheelName: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_name = action.payload;
    },

    setDescription: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].description = action.payload;
    },

    setSubTitle: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].sub_title = action.payload;
    },

    // ISP-9999 (stgport-education-category-discover-wheel-tags): staging had
    // no Categories/Tags state at all in the wheel builder before this ticket.
    setCategories: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].categories = action.payload;
    },

    setWheelTags: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_tags = action.payload;
    },

    setWheelColorPalette: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].palette_id = action.payload.paletteId;
      state.history[state.currentIndex].palette_data =
        action.payload.paletteData;
    },

    setWheelSegments: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_segments = action.payload;
    },

    setAutomaticResizing: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].automatic_resizing = action.payload;
    },

    setContentHeights: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].content_heights = action.payload;
    },

    updateSegmentSuggestedColors: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, suggestedColors } = action.payload;
      if (!id) return;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id ? { ...segment, suggestedColors } : segment
      );
    },

    removeWheelSegment: (state, action) => {
      state.isUndoRedoAction = false;
      const id = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.filter((segment) => segment.id !== id);
    },

    setWheelPortrait: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_center_media = action.payload;
    },

    setCloning: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_cloning = action.payload;
    },

    setSegmentIcon: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].segment_icon = action.payload;
    },

    setIsHidden: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_hidden = action.payload;
    },

    setIsClonable: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_clonable = action.payload;
    },

    // minors-13plus B.14: creator maturity self-label. Payload is the API
    // string ("general" | "mature"), not a boolean, so redux holds exactly what
    // the wheel PUT/PATCH sends and what a GET hydrates back.
    setContentRating: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].content_rating = action.payload;
    },

    setWheelPreview: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_preview = action.payload;
    },

    setIsDraft: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_draft = action.payload;
    },

    setIsLive: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_live = action.payload;
    },

    setVisibility: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].visibility = parseInt(action.payload);
    },

    updateSegmentSetting: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, data } = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id
          ? { ...segment, settings: { ...segment.settings, ...data } }
          : segment
      );
    },

    updateSegmentColorPalette: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, palette, paletteId } = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) => {
        return segment.id == id
          ? {
              ...segment,
              settings: {
                ...segment.settings,
                paletteId: paletteId,
                paletteData: palette,
              },
            }
          : segment;
      });
    },

    updateSegmentValue: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, value } = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id ? { ...segment, value } : segment
      );
    },

    setSidePaneVisible: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isSidePaneVisible = action.payload;
    },

    setToolBarVisible: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isToolBarVisible = action.payload;
    },

    setSegmentSettingVisible: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isSegmentSettingVisible =
        action.payload;
    },

    handleMenuChange: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].selectedMenu = action.payload.num;
    },

    setDesignTabTarget: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].designTabTarget = action.payload;
    },

    clearDesignTabTarget: (state) => {
      state.history[state.currentIndex].designTabTarget = null;
    },

    // V1-RESIZE: the per-Segment Settings pane switches into the pure resize
    // view. Top-level UI field, NOT a history mutation.
    setSegmentResizeView: (state, action) => {
      state.segmentResizeView = !!action.payload;
    },

    // W-A0: the side-pane "Resize Segment" toggle writes here to enter/exit the
    // explicit resize-drag mode. Top-level UI field, NOT a history mutation (no
    // isUndoRedoAction, no snapshot) - it must not be part of undo/redo. Both
    // side-pane toggles and the builder's unmount cleanup dispatch this.
    setResizeSegmentMode: (state, action) => {
      state.resizeSegmentMode = !!action.payload;
    },

    // Per-segment environment fields (SegmentEnvironment pane). Merges `data`
    // into the segment with the matching id, in the CURRENT history entry.
    updateSegmentEnvironment: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, data } = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id ? { ...segment, ...data } : segment
      );
    },

    handleCloseMenu: (state) => {
      state.history[state.currentIndex].selectedMenu = null;
    },

    handleFroalaFocus: (state, action) => {
      state.froala_focus = action.payload;
    },

    setWheelRotateBoxVisibility: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_rotate_box_open = action.payload;
    },

    setTakeScreenshot: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].take_screenshot = action.payload;
    },

    addWheelData: (state, action) => {
      state.isUndoRedoAction = false;
      // state.history[state.currentIndex] = action.payload;

      state.history[state.currentIndex] = {
        ...state.history[state.currentIndex],
        ...action.payload,
      };
    },

    setLockEmails: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].lock_type.permission_lock.emails =
        action.payload;
    },

    setLockEmailPermission: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].lock_type.permission_lock.is_active = action.payload;
    },

    setLockAccessCodes: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[
        state.currentIndex
      ].lock_type.access_code_lock = action.payload;
    },

    setIsSavingWheel: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isSavingWheel = action.payload;
    },

    setWheelId: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].id = action.payload;
    },

    resetBuilder: (state) => {
      state.history = [initialState];
      state.currentIndex = 0;
    },

    setGlobalCornerRounding: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      present.globalSettings.cornerRounding = action.payload;
      // Get the new corner rounding value
      const newCornerRounding = action?.payload;

      if (newCornerRounding !== undefined) {
        // Loop through each segment to update container nodes
        present.wheel_segments = updateWheelSegmentsNodes(
          present.wheel_segments,
          (node) => {
            if (node.type?.resolvedName === "ContainerWidget" && node.props) {
              node.props.radius = newCornerRounding;
            } else if (node?.props?.borderRadius) {
              node.props.borderRadius = newCornerRounding;
            }
          }
        );
      }
      present.isGlobalCornerRoundingApplied = true;
    },

    setGlobalMargin: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      merge(present.globalSettings.margin, action.payload);

      const newMargin = action?.payload;

      // Update nodes in wheel segments
      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node?.props?.margin) {
            node.props.margin = newMargin;
          }
        }
      );

      present.isGlobalMarginApplied = true;
    },

    setGlobalborderThickness: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      present.globalSettings.borderThickness = action.payload;

      const newBorderThickness = action?.payload;

      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node.type?.resolvedName === "ContainerWidget" && node.props) {
            node.props.border.width = newBorderThickness;
          } else if (node?.props?.borderWidth) {
            node.props.borderWidth = newBorderThickness;
          }
        }
      );
      present.isGlobalBorderThicknessApplied = true;
    },

    setGlobalHeadingProps: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      merge(present.globalSettings.headingProps, action.payload);

      const newHeadingProps = action?.payload;

      // Update nodes in wheel segments
      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node?.props?.headingProps) {
            node.props.headingProps = newHeadingProps;
          }
        }
      );

      present.isGlobalHeadingPropsApplied = true;
    },
    setGlobalParagraphProps: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      merge(present.globalSettings.paragraphProps, action.payload);

      const newParagraphProps = action?.payload;

      // Update nodes in wheel segments
      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node?.props?.paragraphProps) {
            node.props.paragraphProps = newParagraphProps;
          }
        }
      );

      present.isGlobalParagraphPropsApplied = true;
    },

    setGlobalLineHeight: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      present.globalSettings.lineHeight = action.payload;

      const newLineHeight = action?.payload;

      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node?.props?.lineHeight) {
            node.props.lineHeight = newLineHeight;
          }
        }
      );
      present.isGlobalLineHeightApplied = true;
    },
    setGlobalSettings: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);

      // Deep-merge so nested objects (like headingProps, paragraphProps, margin) get updated
      // without destroying other keys.
      merge(present.globalSettings, action.payload);

      const newBorderWidth = action?.payload?.borderThickness;
      const newMargin = action?.payload?.margin;
      const newCornerRounding = action?.payload?.cornerRounding;

      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node.type?.resolvedName === "ContainerWidget" && node.props) {
            node.props.border.width = newBorderWidth;
            node.props.radius = newCornerRounding;
            node.props.margin = newMargin;
          } else if (
            (node.type?.resolvedName === "TextWidget" ||
              node.type?.resolvedName === "ImageWithText") &&
            node.props
          ) {
            node.props.lineHeight = action?.payload?.lineHeight;
            node.props.headingProps = action?.payload?.headingProps;
            node.props.paragraphProps = action?.payload?.paragraphProps;
          } else if (
            (node.type?.resolvedName === "DividerWidget" ||
              node.type?.resolvedName === "Divider") &&
            node.props
          ) {
            // For dividers, we only want to apply the margin, NOT thickness
            node.props.margin = newMargin;
          } else if (
            (node.type?.resolvedName === "Image" ||
              node.type?.resolvedName === "ImageWidget") &&
            node.props
          ) {
            // For Image blocks, apply border radius, border width, and margin
            node.props.margin = newMargin;
            node.props.borderRadius = newCornerRounding;
            node.props.borderWidth = newBorderWidth;
            console.log("Applying global settings to Image:", node.props);
          } else {
            // For other components
            if (node.props?.borderRadius !== undefined) {
              node.props.borderRadius = newCornerRounding;
            }
            if (node.props?.borderWidth !== undefined) {
              node.props.borderWidth = newBorderWidth;
            }
            if (node.props?.margin !== undefined) {
              node.props.margin = newMargin;
            }
          }
        }
      );

      present.isGlobalSettingsApplied = true;
    },
    setisGlobalSettingsApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalSettingsApplied =
        action.payload;
    },
    setisisGlobalBorderThicknessApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalBorderThicknessApplied =
        action.payload;
    },
    setisGlobalMarginApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalMarginApplied = action.payload;
    },
    setisGlobalCornerRoundingApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalCornerRoundingApplied =
        action.payload;
    },
    setisGlobalHeadingPropsApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalHeadingPropsApplied =
        action.payload;
    },
    setisGlobalParagraphPropsApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalParagraphPropsApplied =
        action.payload;
    },
    setisGlobalLineHeightApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalLineHeightApplied =
        action.payload;
    },
    // Add this new reducer to mark changes as saved
    markChangesSaved: (state) => {
      state.hasUnsavedChanges = false;
      state.lastSavedIndex = state.currentIndex;
    },
    // Add action to update the character count for the selected segment
    setSelectedSegmentCharCount: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].selected_segment_char_count =
        action.payload;
    },
    addNewSegmentToWheel(state, action) {
      const current = state.history[state.currentIndex];
      const { updatedSegments, newSegment } = action.payload;

      // If updatedSegments are provided, use them; otherwise just mark existing segments as unselected
      const updatedWheelData =
        updatedSegments ||
        current.wheel_segments.map((segment) => ({
          ...segment,
          selected: false,
        }));

      // Generate a unique ID if not provided
      const segmentId =
        newSegment.id ||
        Math.max(...updatedWheelData.map((s) => s.id || 0), 0) + 1;

      // Calculate position for the new segment
      const lastPosition =
        updatedWheelData.length > 0
          ? Math.max(...updatedWheelData.map((s) => s.position || 0))
          : 0;
      const newPosition = lastPosition + 10;

      // Parse nodes if they're in string format
      const nodes =
        typeof newSegment.nodes === "string"
          ? newSegment.nodes
          : JSON.stringify(newSegment.nodes);

      // Create the final new segment with proper attributes
      const finalNewSegment = {
        ...newSegment,
        id: segmentId,
        selected: true,
        position: newSegment.position || newPosition,
        is_modified: true,
        nodes: nodes,
        // Ensure settings are properly set
        settings: {
          ...(newSegment.settings || {}),
          color: newSegment.settings?.color,
        },
      };

      // Update wheel segments with both updated existing segments and new segment
      current.wheel_segments = [...updatedWheelData, finalNewSegment];

      // Mark that we have unsaved changes
      state.hasUnsavedChanges = true;
    },
  },
});

export const {
  undo,
  redo,
  setSegmentResizeView,
  setResizeSegmentMode,
  updateSegmentEnvironment,
  setType,
  setBusinessBoxWheel,
  setIsPrimary,
  setIsPublic,
  setIsLocked,
  setWheelName,
  setDescription,
  setSubTitle,
  setCategories,
  setWheelTags,
  setWheelSegments,
  setAutomaticResizing,
  setContentHeights,
  removeWheelSegment,
  updateSegmentSuggestedColors,
  setWheelPortrait,
  setCloning,
  setSegmentIcon,
  setIsHidden,
  setVisibility,
  updateSegmentSetting,
  updateSegmentValue,
  setSidePaneVisible,
  setToolBarVisible,
  setSegmentSettingVisible,
  handleMenuChange,
  setDesignTabTarget,
  clearDesignTabTarget,
  setWheelPreview,
  handleCloseMenu,
  setWheelRotateBoxVisibility,
  addWheelData,
  setIsSavingWheel,
  resetBuilder,
  pushToHistoryAction,
  recordHistorySnapshot,
  setHistorySuppressed,
  handleFroalaFocus,
  setLockEmails,
  setLockAccessCodes,
  setIsClonable,
  setContentRating,
  setIsLive,
  setGlobalSettings,
  setIsDraft,
  setWheelId,
  updateSegmentColorPalette,
  setisGlobalSettingsApplied,
  setGlobalCornerRounding,
  setGlobalMargin,
  setGlobalborderThickness,
  setisGlobalCornerRoundingApplied,
  setisGlobalMarginApplied,
  setisisGlobalBorderThicknessApplied,
  setisGlobalHeadingPropsApplied,
  setisGlobalParagraphPropsApplied,
  setisGlobalLineHeightApplied,
  setGlobalHeadingProps,
  setGlobalParagraphProps,
  setGlobalLineHeight,
  setWheelColorPalette,
  markChangesSaved,
  setTakeScreenshot,
  setSelectedSegmentCharCount,
  addNewSegmentToWheel,
  setLockEmailPermission,
} = segmentBuilder.actions;

export default segmentBuilder.reducer;
